/**
 * The Terminate control on Tenant Detail, honest about who may use it and what
 * it asks for first.
 *
 * D3 hotfix, Option B (owner decision, 3 Nov 2026). The backend lets only the
 * top operator role, Owner, end a tenant, and only with the tenant's CURRENT
 * name typed exactly and a reason; anything else is a typed 403 or 422. A
 * permission check alone cannot express "Owner" (a permission can be granted
 * to anyone), so this control reads the operator's ROLES, which
 * `/api/admin/capabilities` already returns on `operator`. The backend stays
 * the authority; this only stops the screen offering what the server refuses.
 *
 * - Owner: Terminate opens a confirmation dialog (the repo's Radix Dialog, so
 *   focus is trapped inside while it is open and returned to the Terminate
 *   button when it closes). It needs the tenant's name and a reason. An error
 *   is shown inline beside the field it is about, linked with
 *   `aria-describedby` and announced (`role="alert"`), and focus moves to it.
 * - Anyone else: the control is DISABLED WITH A REASON. It uses `aria-disabled`,
 *   not `disabled`, so it stays in the tab order, and a screen reader announces
 *   the explanation through `aria-describedby`.
 *
 * Suspend is untouched: it lives beside this control and keeps its own gate.
 *
 * THE TENANTS LIST REUSES THIS FILE, not a copy of it: its "Delete" item opens
 * `ConfirmTerminateDialog` (same name + reason rule, same inline errors, same
 * focus handling) for an Owner, and for anyone else shows the same
 * `terminateUnavailableText` explanation on a disabled item.
 */
import React, { useId, useRef, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { canTerminate } from './tenantLifecycle';

/** Mirrors ROLE_OWNER in backend/shared/constants/operator_permissions.py.
 *  A backend test pins this line to that constant. */
export const TOP_OPERATOR_ROLE = 'owner';

/** Mirror MIN_REASON_LENGTH / MAX_REASON_LENGTH in backend/shared/audit/reason.py
 *  (both pinned by backend tests). ONE rule on every door, 4-2000 after trimming. */
export const MIN_TERMINATE_REASON_LENGTH = 4;
export const MAX_TERMINATE_REASON_LENGTH = 2000;

export const TERMINATE_UNAVAILABLE_TEXT =
  'Only an Owner can terminate a tenant. Suspending stops access at once and is reversible.';

export const CHECKING_TEXT = 'Checking your permissions…';

export const NAME_ERROR_TEXT =
  "Type the tenant's name exactly as shown. Letters, case and inner spaces must match.";

export const REASON_ERROR_TEXT = (
  `State why the tenant is being terminated, in ${MIN_TERMINATE_REASON_LENGTH} to `
  + `${MAX_TERMINATE_REASON_LENGTH} characters. It is recorded on the audit receipt.`);

export const REASON_HINT_TEXT = (
  `${MIN_TERMINATE_REASON_LENGTH}-${MAX_TERMINATE_REASON_LENGTH} characters, recorded on the `
  + 'audit receipt before the tenant is terminated.');

export const NO_NAME_TEXT = (
  'This tenant has no name to confirm it by. Set its name in Edit Tenant first, then terminate it.');

/** NFC-normalised and trimmed: the same text the backend compares
 *  (`canonical_text` in tenant_termination_guard.py). */
export const canonicalText = (value) => (
  typeof value === 'string' ? value.normalize('NFC').trim() : '');

/**
 * Why this operator cannot terminate this tenant, for a disabled control's
 * description; null when they can. A tenant with no name cannot be confirmed
 * by name, so even an Owner is told to name it first.
 */
export const terminateUnavailableText = (operator, tenantName) => {
  if (!operator) return CHECKING_TEXT;
  if (!holdsTopRole(operator)) return TERMINATE_UNAVAILABLE_TEXT;
  if (tenantName !== undefined && !canonicalText(tenantName)) return NO_NAME_TEXT;
  return null;
};

export function holdsTopRole(operator) {
  if (!operator || operator.is_active === false) return false;
  return Array.isArray(operator.roles) && operator.roles.includes(TOP_OPERATOR_ROLE);
}

/**
 * The words to show for a failed terminate. Typed refusals arrive as
 * `detail: {error, message, remedy}`; older ones as a plain string. Rendering
 * the object itself would print "[object Object]".
 */
export function refusalText(err, fallback) {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return [detail.message, detail.remedy].filter(Boolean).join(' ') || fallback;
  }
  return (typeof detail === 'string' && detail) || fallback;
}

/** Which field a server refusal is about, when it says (`detail.field`). */
export function refusalField(err) {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  return detail && typeof detail === 'object' ? detail.field || null : null;
}

/**
 * The same rule the backend applies: both values NFC-normalised and trimmed;
 * the name must then match exactly; the reason must be 4-2000 characters.
 * Returns `{confirm_tenant_name?: msg, reason?: msg}`; empty means acceptable.
 */
export function validateConfirmation(tenantName, typedName, reason) {
  const errors = {};
  const expected = canonicalText(tenantName);
  if (!expected || canonicalText(typedName) !== expected) {
    errors.confirm_tenant_name = NAME_ERROR_TEXT;
  }
  const cleaned = canonicalText(reason);
  if (cleaned.length < MIN_TERMINATE_REASON_LENGTH || cleaned.length > MAX_TERMINATE_REASON_LENGTH) {
    errors.reason = REASON_ERROR_TEXT;
  }
  return errors;
}

const TERMINATE_DESCRIPTION = (
  'Terminating ends the tenant: its users lose access, and it cannot be reactivated by an '
  + 'ordinary operation. Its data is kept. Suspend instead if you may need it back.');

/**
 * The confirmation an Owner gives before a tenant is ended. Exported for the
 * Tenants list; the copy props let it say "Delete" there (a soft delete, which
 * terminates and hides) without a second dialog. The reason rule is the one
 * rule every door applies.
 *
 * While a request is in flight the fields go read-only and the buttons
 * `aria-disabled`, rather than `disabled`, so focus is never dropped to the
 * page behind the dialog.
 */
export function ConfirmTerminateDialog({
  open, tenantName, onClose, onTerminate, returnFocusTo,
  title, description = TERMINATE_DESCRIPTION, submitLabel = 'Terminate tenant',
}) {
  const ids = { name: useId(), nameError: useId(), reason: useId(), reasonError: useId(),
    reasonHint: useId(), formError: useId() };
  const nameRef = useRef(null);
  const reasonRef = useRef(null);
  const formErrorRef = useRef(null);
  const [typedName, setTypedName] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTypedName(''); setReason(''); setErrors({}); setFormError(null); setSubmitting(false);
  };

  const focusField = (field) => {
    const el = field === 'reason' ? reasonRef.current : nameRef.current;
    if (el) el.focus();
  };

  const submit = async (event) => {
    if (event) event.preventDefault();
    if (submitting) return;
    const found = validateConfirmation(tenantName, typedName, reason);
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length) {
      focusField(found.confirm_tenant_name ? 'confirm_tenant_name' : 'reason');
      return;
    }
    setSubmitting(true);
    try {
      await onTerminate({ confirmTenantName: canonicalText(typedName), reason: canonicalText(reason) });
      reset();
      onClose();
    } catch (err) {
      setSubmitting(false);
      const field = refusalField(err);
      const text = refusalText(err, 'The tenant could not be terminated.');
      if (field === 'confirm_tenant_name' || field === 'reason') {
        setErrors({ [field]: text });
        focusField(field);
      } else {
        setFormError(text);
        // Announced AND focused, so a keyboard user lands on what went wrong.
        setTimeout(() => { if (formErrorRef.current) formErrorRef.current.focus(); }, 0);
      }
    }
  };

  const describe = (field, errorId) => (errors[field] ? errorId : undefined);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) { reset(); onClose(); } }}>
      <DialogContent
        data-testid="terminate-dialog"
        onOpenAutoFocus={(e) => { e.preventDefault(); if (nameRef.current) nameRef.current.focus(); }}
        onCloseAutoFocus={(e) => {
          // Back to the control that opened it, explicitly rather than by luck.
          // Without one, Radix restores what was focused before it opened.
          if (returnFocusTo && returnFocusTo.current && returnFocusTo.current.isConnected !== false) {
            e.preventDefault();
            returnFocusTo.current.focus();
          }
        }}
      >
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>{title || `Terminate ${tenantName}?`}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor={ids.name}>
                Type <span className="font-semibold text-foreground">{tenantName}</span> to confirm
              </Label>
              <Input
                id={ids.name}
                ref={nameRef}
                autoComplete="off"
                spellCheck={false}
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                aria-invalid={errors.confirm_tenant_name ? 'true' : 'false'}
                aria-describedby={describe('confirm_tenant_name', ids.nameError)}
                readOnly={submitting}
                data-testid="terminate-confirm-name"
              />
              {errors.confirm_tenant_name && (
                <p id={ids.nameError} role="alert" className="text-xs text-destructive-text"
                   data-testid="terminate-confirm-name-error">
                  {errors.confirm_tenant_name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.reason}>Reason (required)</Label>
              <p id={ids.reasonHint} className="text-xs text-muted-foreground"
                 data-testid="terminate-reason-hint">
                {REASON_HINT_TEXT}
              </p>
              <Textarea
                id={ids.reason}
                ref={reasonRef}
                rows={3}
                maxLength={MAX_TERMINATE_REASON_LENGTH}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                aria-invalid={errors.reason ? 'true' : 'false'}
                aria-describedby={[ids.reasonHint, describe('reason', ids.reasonError)]
                  .filter(Boolean).join(' ')}
                readOnly={submitting}
                data-testid="terminate-reason"
              />
              {errors.reason && (
                <p id={ids.reasonError} role="alert" className="text-xs text-destructive-text"
                   data-testid="terminate-reason-error">
                  {errors.reason}
                </p>
              )}
            </div>
            {formError && (
              <p id={ids.formError} ref={formErrorRef} role="alert" tabIndex={-1}
                 className="text-sm text-destructive-text focus:outline-none"
                 data-testid="terminate-form-error">
                {formError}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" aria-disabled={submitting ? 'true' : undefined}
                    onClick={() => { if (!submitting) { reset(); onClose(); } }}
                    data-testid="terminate-cancel">
              Cancel
            </Button>
            <Button type="submit" variant="destructive" aria-disabled={submitting ? 'true' : undefined}
                    aria-busy={submitting ? 'true' : undefined} data-testid="terminate-submit">
              {submitting
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                : <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TerminateTenantControl({
  status, operator, busy, onTerminate, tenantName,
}) {
  const reasonId = useId();
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  if (!canTerminate(status)) return null;

  const unavailable = terminateUnavailableText(operator, tenantName === undefined ? undefined : tenantName || '');
  if (!unavailable) {
    return (
      <>
        <Button ref={triggerRef} variant="outline" className="text-destructive-text border-destructive/30"
                onClick={() => setOpen(true)} disabled={busy} aria-haspopup="dialog"
                data-testid="lifecycle-terminate">
          <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" /> Terminate
        </Button>
        <ConfirmTerminateDialog open={open} tenantName={tenantName} onTerminate={onTerminate}
                                onClose={() => setOpen(false)} returnFocusTo={triggerRef} />
      </>
    );
  }

  const reason = unavailable;
  return (
    <span className="inline-flex flex-col gap-1" data-testid="lifecycle-terminate-unavailable">
      <Button variant="outline" aria-disabled="true" aria-describedby={reasonId}
              className="text-muted-foreground border-border opacity-60 cursor-not-allowed"
              onClick={(event) => event.preventDefault()}
              data-testid="lifecycle-terminate">
        <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" /> Terminate
      </Button>
      <span id={reasonId} className="text-xs text-muted-foreground max-w-xs">{reason}</span>
    </span>
  );
}
